/* AI Analysis — Claude-powered "Quick Take" verdict with collapsible deep dive.
   ────────────────────────────────────────────────────────────────────────────
   Pure proxy in front of `/api/ai/analyse`. No third-party keys in the
   browser. If the backend returns 503, the UI shows an amber "API key
   required" callout instead of crashing. */
import { useState, useEffect, useRef } from "react";
import { inputStyle } from "./_shared";
import { usePrices } from "./_prices";
import TradeApprovalCard from "./TradeApprovalCard";

const PRESETS = ["NVDA", "AAPL", "MSFT", "META", "AMZN", "GOOGL"];

// ── Local design tokens. Kept local so the design system can be tweaked
// per-tab without touching _shared.jsx.
const T = {
  card:       { background:"#ffffff", border:"1px solid #e2e8f0", borderRadius:8, padding:"16px 20px", boxShadow:"0 1px 3px rgba(0,0,0,0.06)" },
  sectionLab: { fontSize:10, fontWeight:500, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.08em" },
  primaryBtn: { background:"#4f46e5", color:"#fff", border:"none", borderRadius:6, padding:"8px 18px", fontSize:13, fontWeight:600, cursor:"pointer", display:"inline-flex", alignItems:"center", gap:6 },
};

// Verdict palette — exact colors from the spec
const VERDICT_COLORS = {
  "STRONG BUY":  { bg:"#dcfce7", color:"#15803d", border:"2px solid #16a34a", dot:"#16a34a" },
  "BUY":         { bg:"#f0fdf4", color:"#16a34a", border:"2px solid #86efac", dot:"#16a34a" },
  "NEUTRAL":     { bg:"#f8fafc", color:"#64748b", border:"2px solid #cbd5e1", dot:"#64748b" },
  "SELL":        { bg:"#fff1f2", color:"#dc2626", border:"2px solid #fca5a5", dot:"#dc2626" },
  "STRONG SELL": { bg:"#fee2e2", color:"#991b1b", border:"2px solid #dc2626", dot:"#dc2626" },
};
const verdictPalette = (v) => VERDICT_COLORS[(v || "NEUTRAL").toUpperCase()] || VERDICT_COLORS.NEUTRAL;

const BIAS_PILL = {
  Long:    { bg:"#dcfce7", color:"#15803d" },
  Short:   { bg:"#fee2e2", color:"#991b1b" },
  Neutral: { bg:"#f1f5f9", color:"#64748b" },
};

const OPT_BADGE = {
  // Debit Spreads — indigo
  "Call Debit Spread":  { bg:"#e0e7ff", color:"#4f46e5" },
  "Put Debit Spread":   { bg:"#ffedd5", color:"#ea580c" },
  // Credit Spreads — amber
  "Call Credit Spread": { bg:"#fef3c7", color:"#d97706" },
  "Put Credit Spread":  { bg:"#fef3c7", color:"#d97706" },
  // Iron Condor — purple
  "Iron Condor":        { bg:"#f3e8ff", color:"#7c3aed" },
  // Income plays — gray
  "Covered Call":       { bg:"#f1f5f9", color:"#64748b" },
  "Cash Secured Put":   { bg:"#f1f5f9", color:"#64748b" },
  // Long single legs / volatility — green
  "Long Call":          { bg:"#dcfce7", color:"#16a34a" },
  "Long Straddle":      { bg:"#dcfce7", color:"#16a34a" },
  "Long Strangle":      { bg:"#dcfce7", color:"#16a34a" },
  // Long Put — orange (bearish directional)
  "Long Put":           { bg:"#ffedd5", color:"#ea580c" },
  // Short / undefined-risk — red
  "Short Call":         { bg:"#fee2e2", color:"#dc2626" },
  "Short Put":          { bg:"#fee2e2", color:"#dc2626" },
  "Short Straddle":     { bg:"#fee2e2", color:"#dc2626" },
  "Short Strangle":     { bg:"#fee2e2", color:"#dc2626" },
  // Skip — muted gray
  "Skip options":       { bg:"#f1f5f9", color:"#94a3b8" },
};

// R:R coloring — green if 2.0+, amber otherwise. Parse the leading float.
const rrColor = (rr) => {
  const m = String(rr || "").match(/(-?\d+(?:\.\d+)?)/);
  const num = m ? parseFloat(m[1]) : 0;
  return num >= 2 ? "#16a34a" : "#d97706";
};

function VerdictSkeleton() {
  return (
    <div data-testid="ai-skeleton" style={{ ...T.card, padding:"20px 24px" }}>
      <div className="ai-pulse" style={{ display:"flex", alignItems:"center", gap:24, flexWrap:"wrap" }}>
        <div style={{ width:160, height:44, borderRadius:8, background:"#f1f5f9" }} />
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          <div style={{ width:80, height:10, borderRadius:3, background:"#f1f5f9" }} />
          <div style={{ width:200, height:6, borderRadius:3, background:"#f1f5f9" }} />
          <div style={{ width:60, height:18, borderRadius:999, background:"#f1f5f9" }} />
        </div>
        <div style={{ flex:1, minWidth:200, display:"flex", flexDirection:"column", gap:10 }}>
          <div style={{ width:30, height:8, background:"#f1f5f9", borderRadius:3 }} />
          <div style={{ width:"60%", height:12, background:"#f1f5f9", borderRadius:4 }} />
          <div style={{ width:"80%", height:12, background:"#f1f5f9", borderRadius:4 }} />
          <div style={{ width:"70%", height:12, background:"#f1f5f9", borderRadius:4 }} />
        </div>
      </div>
      <style>{`
        @keyframes ai-pulse-fade { 0%,100% { opacity: 0.4; } 50% { opacity: 1; } }
        .ai-pulse, .ai-pulse > * { animation: ai-pulse-fade 1.5s ease-in-out infinite; }
      `}</style>
    </div>
  );
}

function VerdictCard({ a }) {
  const v = (a.verdict || "NEUTRAL").toUpperCase();
  const palette = verdictPalette(v);
  const conf = Math.max(0, Math.min(100, parseInt(a.confidence ?? 0, 10) || 0));
  const bias = a.bias || "Neutral";
  const biasStyle = BIAS_PILL[bias] || BIAS_PILL.Neutral;
  const reasons = Array.isArray(a.reasons) ? a.reasons.slice(0, 3) : [];

  return (
    <div data-testid="ai-verdict-card" style={{ ...T.card, padding:"20px 24px" }}>
      <div
        className="ai-verdict-grid"
        style={{
          display:"grid",
          gridTemplateColumns:"auto 1fr 1.4fr",
          alignItems:"center",
          columnGap:32,
          rowGap:16,
        }}
      >
        {/* Left column — Verdict badge + VERDICT sub-label */}
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:6 }}>
          <div data-testid="ai-verdict-badge" style={{
            background: palette.bg, color: palette.color, border: palette.border,
            padding:"10px 20px", borderRadius:8,
            fontSize:18, fontWeight:800, letterSpacing:"0.04em", textTransform:"uppercase",
            whiteSpace:"nowrap",
          }}>{v}</div>
          <div style={{ ...T.sectionLab, fontSize:10 }}>Verdict</div>
        </div>

        {/* Centre column — Confidence bar stacked above bias pill */}
        <div style={{ display:"flex", flexDirection:"column", gap:8, minWidth:200 }}>
          <div style={T.sectionLab}>Confidence</div>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ flex:1, maxWidth:200, height:6, borderRadius:3, background:"#f1f5f9", overflow:"hidden" }}>
              <div data-testid="ai-confidence-fill" style={{
                width: `${conf}%`, height:"100%",
                background: palette.dot, borderRadius:3,
                transition:"width 0.3s ease",
              }} />
            </div>
            <span data-testid="ai-confidence-pct" style={{ fontFamily:"monospace", fontWeight:700, fontSize:12, color:"#0f172a" }}>{conf}%</span>
          </div>
          <div data-testid="ai-bias-pill" style={{
            alignSelf:"flex-start", background: biasStyle.bg, color: biasStyle.color,
            padding:"3px 10px", borderRadius:999, fontSize:11, fontWeight:600,
          }}>Bias: {bias}</div>
        </div>

        {/* Right column — Why reasons + divider + Key risk */}
        <div>
          <div style={{ ...T.sectionLab, marginBottom:6 }}>Why</div>
          {reasons.length === 0 ? (
            <div style={{ fontSize:12, color:"#94a3b8", fontStyle:"italic" }}>No reasons returned</div>
          ) : reasons.map((r, i) => (
            <div key={i} data-testid={`ai-reason-${i}`} style={{ display:"flex", gap:6, alignItems:"flex-start", marginBottom:4 }}>
              <span style={{
                display:"inline-block", width:6, height:6, borderRadius:"50%",
                background: palette.dot, marginTop:7, flexShrink:0,
              }} />
              <span style={{ fontSize:12, color:"#374151", lineHeight:1.5 }}>{r}</span>
            </div>
          ))}
          {a.key_risk && (
            <>
              <div style={{ borderTop:"1px solid #f1f5f9", margin:"8px 0" }} />
              <div style={{ fontSize:10, fontWeight:500, textTransform:"uppercase", letterSpacing:"0.08em", color:"#dc2626", marginBottom:4 }}>Key Risk</div>
              <div data-testid="ai-key-risk" style={{ fontSize:12, color:"#dc2626", fontStyle:"italic", lineHeight:1.5 }}>{a.key_risk}</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function LevelRow({ label, value, valueColor, mono = true }) {
  return (
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"6px 0", borderBottom:"1px solid #f1f5f9" }}>
      <span style={{ fontSize:11, color:"#64748b" }}>{label}</span>
      <span style={{ fontSize:13, fontFamily: mono ? "monospace" : "inherit", fontWeight:700, color: valueColor || "#0f172a" }}>{value || "—"}</span>
    </div>
  );
}

function TradeLevelsCard({ levels }) {
  const l = levels || {};
  return (
    <div data-testid="ai-trade-levels" style={T.card}>
      <div style={{ ...T.sectionLab, marginBottom:12 }}>Trade Levels</div>
      <LevelRow label="Entry"  value={l.entry}  valueColor="#4f46e5" />
      <LevelRow label="Stop"   value={l.stop}   valueColor="#dc2626" />
      <LevelRow label="Target" value={l.target} valueColor="#16a34a" />
      <LevelRow label="R:R"    value={l.rr}     valueColor={rrColor(l.rr)} />
    </div>
  );
}

function OptionsCard({ opt }) {
  const o = opt || {};
  const dir = o.direction || "Skip options";
  const badge = OPT_BADGE[dir] || OPT_BADGE["Skip options"];
  const isSkip = dir === "Skip options";

  return (
    <div data-testid="ai-options-card" style={T.card}>
      <div style={{ ...T.sectionLab, marginBottom:12 }}>Options Play</div>
      <div data-testid="ai-options-direction" style={{
        display:"inline-block", background: badge.bg, color: badge.color,
        fontSize:12, fontWeight:700, padding:"4px 12px", borderRadius:6, marginBottom:10,
      }}>{dir}</div>

      {isSkip ? (
        <div data-testid="ai-options-skip" style={{ textAlign:"center", fontSize:12, color:"#94a3b8", fontStyle:"italic", padding:"12px 0" }}>
          Options not recommended for this setup
        </div>
      ) : (
        <>
          <LevelRow label="Strike"            value={o.strike}    valueColor="#0f172a" />
          <LevelRow label="Expiry"            value={o.expiry}    valueColor="#0f172a" mono={false} />
          <LevelRow label="Max risk/contract" value={o.max_risk}  valueColor="#dc2626" />
          {o.rationale && (
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", padding:"8px 0 0", gap:12 }}>
              <span style={{ fontSize:11, color:"#64748b", flexShrink:0 }}>Why</span>
              <span style={{ fontSize:11, color:"#64748b", fontStyle:"italic", textAlign:"right", lineHeight:1.5 }}>{o.rationale}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function DeepAnalysis({ text }) {
  const [open, setOpen] = useState(false);
  if (!text) return null;
  return (
    <div data-testid="ai-deep-card" style={{ ...T.card, padding:0 }}>
      <div
        data-testid="ai-deep-toggle"
        onClick={() => setOpen(o => !o)}
        style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"14px 20px", cursor:"pointer" }}
      >
        <div style={T.sectionLab}>Deep Analysis</div>
        <div style={{ fontSize:11, color:"#94a3b8" }}>{open ? "▴ Hide" : "▾ Show"}</div>
      </div>
      <div style={{
        maxHeight: open ? 2000 : 0,
        overflow:"hidden",
        transition:"max-height 0.2s ease",
      }}>
        <div data-testid="ai-deep-text" style={{ padding:"0 20px 16px", fontSize:13, color:"#374151", lineHeight:1.8, whiteSpace:"pre-wrap" }}>
          {text}
        </div>
      </div>
    </div>
  );
}

function Disclaimer({ text }) {
  return (
    <div data-testid="ai-disclaimer" style={{
      background:"#fffbeb", border:"1px solid #fef08a", borderRadius:6,
      padding:"6px 14px", fontSize:10, color:"#92400e",
    }}>
      {text || "Educational only. Not financial advice."}
    </div>
  );
}

// Shared render for a propose-trade outcome (loading / error / approval
// card / risk-block). Used by both the Morning Scan rows and the manual
// "Propose this trade" button so the gate looks identical from either entry.
function ProposeOutcome({ proposing, result, error, onResolved, onDismiss }) {
  if (proposing) {
    return (
      <div data-testid="ai-propose-loading" style={{ ...T.card, fontSize:13, color:"#4f46e5" }}>
        Checking risk…
      </div>
    );
  }
  if (error) {
    return (
      <div data-testid="ai-propose-error" style={{ ...T.card, borderColor:"#dc2626", color:"#dc2626", fontSize:13 }}>
        Error: {error}
      </div>
    );
  }
  if (!result) return null;
  if (result.approved && result.proposal) {
    return <TradeApprovalCard proposal={result.proposal} onResolved={onResolved} />;
  }
  return (
    <div data-testid="ai-propose-blocked" style={{ ...T.card, border:"1px solid #fca5a5", background:"#fef2f2", padding:"14px 18px" }}>
      <div style={{ fontSize:13, fontWeight:700, color:"#991b1b", marginBottom:8 }}>🚫 Risk engine blocked this trade</div>
      {(result.blocks || []).map((b, i) => (
        <div key={i} data-testid={`ai-propose-block-${i}`} style={{ fontSize:12, color:"#991b1b", marginBottom:3, display:"flex", gap:6 }}>
          <span>•</span><span>{b}</span>
        </div>
      ))}
      {(result.warnings || []).map((w, i) => (
        <div key={`w-${i}`} style={{ fontSize:11, color:"#92400e", marginTop:3 }}>⚠ {w}</div>
      ))}
      <button
        data-testid="ai-propose-dismiss"
        onClick={onDismiss}
        style={{ marginTop:10, background:"transparent", border:"1px solid #cbd5e1", color:"#64748b", borderRadius:6, padding:"6px 14px", fontSize:12, fontWeight:600, cursor:"pointer" }}
      >
        Dismiss
      </button>
    </div>
  );
}

// Phase 4 — S&P 500 Morning Scan. Two-stage server-side pipeline (Polygon
// screen → Claude analysis) cached daily; this section just renders the
// cached BUY / STRONG BUY setups as rows, each with a one-tap Propose
// button that runs the same Entry Approval gate as the manual flow.
function MorningScanSection({ scan, loading, running, error, onRun, onPropose, children }) {
  const scanned = scan?.scanned_today;
  const setups  = Array.isArray(scan?.setups) ? scan.setups : [];
  return (
    <div data-testid="ai-morning-scan" style={{ ...T.card, padding:"16px 20px" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, flexWrap:"wrap" }}>
        <div>
          <div style={{ fontSize:15, fontWeight:800, color:"#0f172a", display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
            🌅 Morning Scan
            <span style={{ fontSize:11, fontWeight:600, color:"#94a3b8", textTransform:"uppercase", letterSpacing:"0.06em" }}>S&amp;P 500 AI Setup Finder</span>
          </div>
          <div data-testid="scan-status" style={{ fontSize:11, color:"#64748b", marginTop:4 }}>
            {loading
              ? "Loading today's scan…"
              : scanned
                ? `Scanned ${scan.scanned ?? 0} tickers · ${scan.stage1_count ?? 0} candidates · ${setups.length} setup${setups.length === 1 ? "" : "s"} ≥78%`
                : "No scan yet today — run one to find BUY / STRONG BUY setups."}
          </div>
        </div>
        <button
          data-testid="scan-run-btn"
          onClick={onRun}
          disabled={running}
          style={{ ...T.primaryBtn, background: running ? "#a5b4fc" : "#4f46e5", cursor: running ? "default" : "pointer" }}
        >
          {running ? "Scanning…" : (scanned ? "↻ Re-run scan" : "▸ Run scan")}
        </button>
      </div>

      {error && (
        <div data-testid="scan-error" style={{ marginTop:10, color:"#dc2626", fontSize:12 }}>Error: {error}</div>
      )}

      {scanned && setups.length === 0 && !running && (
        <div data-testid="scan-empty" style={{ marginTop:12, fontSize:12, color:"#94a3b8", fontStyle:"italic" }}>
          No BUY / STRONG BUY setups cleared the 78% confidence bar today.
        </div>
      )}

      {setups.length > 0 && (
        <div style={{ display:"flex", flexDirection:"column", gap:8, marginTop:12 }}>
          {setups.map((s) => {
            const pal = verdictPalette(s.verdict);
            return (
              <div
                key={s.ticker}
                data-testid={`scan-row-${s.ticker}`}
                style={{ display:"flex", alignItems:"center", gap:12, flexWrap:"wrap", padding:"10px 12px", border:"1px solid #e2e8f0", borderRadius:8, background:"#fff" }}
              >
                <span style={{ fontFamily:"monospace", fontWeight:800, fontSize:14, color:"#0f172a", minWidth:56 }}>{s.ticker}</span>
                <span style={{ background:pal.bg, color:pal.color, border:pal.border, padding:"2px 10px", borderRadius:6, fontSize:11, fontWeight:800, textTransform:"uppercase", whiteSpace:"nowrap" }}>{s.verdict}</span>
                <span style={{ fontFamily:"monospace", fontWeight:700, fontSize:12, color:pal.dot }}>{s.confidence}%</span>
                {s.price != null && (
                  <span style={{ fontFamily:"monospace", fontSize:12, color:"#475569" }}>${Number(s.price).toFixed(2)}</span>
                )}
                {s.gap_pct != null && (
                  <span style={{ fontFamily:"monospace", fontSize:12, color: s.gap_pct >= 0 ? "#16a34a" : "#dc2626" }}>
                    {s.gap_pct >= 0 ? "+" : ""}{Number(s.gap_pct).toFixed(2)}%
                  </span>
                )}
                {s.key_risk && (
                  <span style={{ fontSize:11, color:"#94a3b8", fontStyle:"italic", maxWidth:240, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{s.key_risk}</span>
                )}
                <button
                  data-testid={`scan-propose-${s.ticker}`}
                  onClick={() => onPropose(s.ticker)}
                  style={{ ...T.primaryBtn, marginLeft:"auto", padding:"6px 14px", fontSize:12 }}
                >▸ Propose</button>
              </div>
            );
          })}
        </div>
      )}

      {children && <div style={{ marginTop:12 }}>{children}</div>}
    </div>
  );
}

export default function AIAnalysisTab() {
  const [currentTicker, setCurrentTicker] = useState("NVDA");
  const [customInput, setCustomInput]     = useState("");
  const [customTickers, setCustomTickers] = useState([]); // user-added pills, session-scoped
  const [analysis, setAnalysis]           = useState(null);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState(null);
  const [needsKey, setNeedsKey]           = useState(false);
  const [companyName, setCompanyName]     = useState("");
  // Phase 3 — Entry Approval gate state.
  const [proposing, setProposing]         = useState(false);
  const [proposeResult, setProposeResult] = useState(null); // /api/ai/propose-trade response
  const [proposeError, setProposeError]   = useState(null);
  const [proposeSource, setProposeSource] = useState(null); // "manual" | "scan" — where the propose was triggered
  // Morning Scan (S&P 500 AI setup finder) state.
  const [scan, setScan]               = useState(null);     // /api/scan/morning response
  const [scanLoading, setScanLoading] = useState(true);
  const [scanRunning, setScanRunning] = useState(false);
  const [scanError, setScanError]     = useState(null);

  // Pull live prices from the shared PriceProvider context. The provider
  // exposes a map keyed by ticker, plus `addTicker` so we can register
  // arbitrary custom symbols (e.g. "HAWK") into the live feed.
  const ctx = usePrices();
  const p = ctx?.prices?.[currentTicker];
  const addTicker = ctx?.addTicker;
  const nameAbortRef = useRef(null);

  // Whenever the active ticker changes: register it with the live price
  // feed (so the price ribbon updates for custom symbols) and resolve its
  // company name via the existing /api/stock/search autocomplete endpoint.
  useEffect(() => {
    if (!currentTicker) return;
    if (typeof addTicker === "function") {
      try { addTicker(currentTicker); } catch { /* noop */ }
    }
    // Cancel any in-flight name lookup from the previous ticker
    if (nameAbortRef.current) {
      try { nameAbortRef.current.abort(); } catch { /* noop */ }
    }
    const ac = new AbortController();
    nameAbortRef.current = ac;
    setCompanyName("");
    const API = process.env.REACT_APP_BACKEND_URL || "";
    fetch(`${API}/api/stock/search?q=${encodeURIComponent(currentTicker)}`, {
      credentials: "include", signal: ac.signal,
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        const hit = (data?.results || []).find(
          r => (r.symbol || "").toUpperCase() === currentTicker.toUpperCase()
        ) || (data?.results || [])[0];
        if (hit?.name) setCompanyName(hit.name);
      })
      .catch(() => { /* silent — name is a nice-to-have */ });
    return () => { try { ac.abort(); } catch { /* noop */ } };
  }, [currentTicker, addTicker]);

  // `overrideTicker` lets the Set button trigger an immediate analysis
  // run on the freshly-typed custom ticker without waiting for React's
  // state update to flush — fixes the "click Set, nothing happens" bug.
  // Guarded against React synthetic-event leakage when this is wired
  // directly as an onClick handler (e2 events aren't strings).
  const run = async (overrideTicker = null) => {
    const overrideStr = (typeof overrideTicker === "string") ? overrideTicker : "";
    const ticker = (overrideStr || currentTicker || "").toUpperCase().trim();
    if (!ticker) return;
    setLoading(true); setError(null); setNeedsKey(false); setAnalysis(null);
    setProposeResult(null); setProposeError(null);
    try {
      const API = process.env.REACT_APP_BACKEND_URL || "";
      const r   = await fetch(`${API}/api/ai/analyse`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ticker }),
      });
      if (r.status === 503) { setNeedsKey(true); setLoading(false); return; }
      if (!r.ok) {
        const detail = await r.json().catch(() => ({}));
        throw new Error(detail?.detail || `HTTP ${r.status}`);
      }
      const data = await r.json();
      setAnalysis(data);
    } catch (e) {
      setError(e.message || "Analysis failed");
    } finally {
      setLoading(false);
    }
  };

  // Reset any open proposal whenever the active ticker changes — a proposal
  // is only valid for the ticker it was generated against.
  useEffect(() => {
    setProposeResult(null);
    setProposeError(null);
    setProposeSource(null);
  }, [currentTicker]);

  // Phase 3 — send a ticker through the Entry Approval gate (live price +
  // AI analysis + risk engine, server-side in one call). `source` records
  // whether the trigger was the manual "Propose this trade" button or a
  // Morning Scan row, so the outcome renders next to its trigger.
  const proposeTrade = async (overrideTicker = null, source = "manual") => {
    const ticker = ((typeof overrideTicker === "string" && overrideTicker) ? overrideTicker : currentTicker || "")
      .toUpperCase().trim();
    if (!ticker) return;
    setProposeSource(source);
    setProposing(true); setProposeError(null); setProposeResult(null);
    try {
      const API = process.env.REACT_APP_BACKEND_URL || "";
      const r = await fetch(`${API}/api/ai/propose-trade`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ticker }),
      });
      if (!r.ok) {
        const detail = await r.json().catch(() => ({}));
        throw new Error(detail?.detail || `HTTP ${r.status}`);
      }
      setProposeResult(await r.json());
    } catch (e) {
      setProposeError(e.message || "Failed to propose trade");
    } finally {
      setProposing(false);
    }
  };

  // Morning Scan — load today's cached scan on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setScanLoading(true);
      try {
        const API = process.env.REACT_APP_BACKEND_URL || "";
        const r = await fetch(`${API}/api/scan/morning`, { credentials: "include" });
        if (r.ok && !cancelled) setScan(await r.json());
      } catch { /* silent — section just shows the Run button */ }
      finally { if (!cancelled) setScanLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  // Force a fresh scan (Stage 1 Polygon → Stage 2 Claude, server-side).
  const runScan = async () => {
    setScanRunning(true); setScanError(null);
    try {
      const API = process.env.REACT_APP_BACKEND_URL || "";
      const r = await fetch(`${API}/api/scan/run`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      if (!r.ok) {
        const detail = await r.json().catch(() => ({}));
        throw new Error(detail?.detail || `HTTP ${r.status}`);
      }
      const data = await r.json();
      setScan({ scanned_today: true, ...data });
    } catch (e) {
      setScanError(e.message || "Scan failed");
    } finally {
      setScanRunning(false);
    }
  };

  const submitCustom = () => {
    const t = (customInput || "").toUpperCase().trim();
    if (!t) return;
    // Add as a pill (no dupes — works whether the ticker matches a preset
    // or an existing custom). PRESETS are always shown so we only push to
    // customTickers when the ticker isn't already in either list.
    const alreadyPreset = PRESETS.includes(t);
    setCustomTickers(prev => (alreadyPreset || prev.includes(t)) ? prev : [...prev, t]);
    setCurrentTicker(t);
    setCustomInput("");
    setAnalysis(null);
    // Auto-run analysis on the new ticker — the override avoids racing
    // React's setCurrentTicker batching.
    run(t);
  };

  const removeCustomTicker = (t) => {
    setCustomTickers(prev => prev.filter(x => x !== t));
    // If the active ticker is the one being removed, fall back to NVDA.
    if (currentTicker === t) {
      setCurrentTicker("NVDA");
      setAnalysis(null);
    }
  };

  // Pills row data: presets first, then session-added custom tickers (each
  // with a × remove handle). Both render identically; only customs get the ×.
  const pillStyle = (sel) => ({
    background: sel ? "#4f46e5" : "#fff",
    color:      sel ? "#fff"    : "#64748b",
    border:     sel ? "1px solid #4f46e5" : "1px solid #e2e8f0",
    borderRadius: 999, padding:"4px 12px", fontSize:11, fontWeight:600,
    cursor:"pointer", letterSpacing:"0.05em", fontFamily:"monospace",
    display:"inline-flex", alignItems:"center", gap:6,
  });

  const hasResult = analysis && !loading;
  const showInfoBar = !needsKey;

  return (
    <div data-testid="ai-analysis-tab" style={{ display:"flex", flexDirection:"column", gap:12 }}>
      {/* ── ROW 0 — S&P 500 Morning Scan (Phase 4) ───────────────────── */}
      <MorningScanSection
        scan={scan}
        loading={scanLoading}
        running={scanRunning}
        error={scanError}
        onRun={runScan}
        onPropose={(t) => proposeTrade(t, "scan")}
      >
        {proposeSource === "scan" && (
          <ProposeOutcome
            proposing={proposing}
            result={proposeResult}
            error={proposeError}
            onResolved={() => { setProposeResult(null); setProposeSource(null); }}
            onDismiss={() => { setProposeResult(null); setProposeSource(null); }}
          />
        )}
      </MorningScanSection>

      {/* ── ROW 1 — Ticker pills + custom input + GO ─────────────────── */}
      <div style={T.card}>
        <div style={{ ...T.sectionLab, marginBottom:10 }}>Ticker</div>
        <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
          {PRESETS.map(t => {
            const sel = t === currentTicker;
            return (
              <button
                key={t} data-testid={`ai-preset-${t}`}
                onClick={() => { setCurrentTicker(t); setAnalysis(null); }}
                style={pillStyle(sel)}
              >{t}</button>
            );
          })}
          {customTickers.map(t => {
            const sel = t === currentTicker;
            return (
              <span
                key={t} data-testid={`ai-custom-pill-${t}`}
                style={pillStyle(sel)}
                onClick={() => { setCurrentTicker(t); setAnalysis(null); }}
              >
                {t}
                <button
                  data-testid={`ai-custom-remove-${t}`}
                  onClick={(e) => { e.stopPropagation(); removeCustomTicker(t); }}
                  aria-label={`Remove ${t}`}
                  style={{
                    background:"transparent", border:"none",
                    color: sel ? "rgba(255,255,255,0.85)" : "#94a3b8",
                    cursor:"pointer", fontSize:13, lineHeight:1, padding:"0 2px",
                    fontFamily:"inherit",
                  }}
                >×</button>
              </span>
            );
          })}
          <input
            data-testid="ai-custom-input"
            value={customInput}
            onChange={(e) => setCustomInput(e.target.value.toUpperCase())}
            onKeyDown={(e) => { if (e.key === "Enter") submitCustom(); }}
            placeholder="custom"
            style={{ ...inputStyle, width:90, fontFamily:"monospace", textTransform:"uppercase" }}
          />
          <button
            data-testid="ai-custom-submit" onClick={submitCustom}
            style={{ background:"#4f46e5", color:"#fff", border:"1px solid #4f46e5", borderRadius:6, padding:"6px 14px", fontSize:12, fontWeight:600, cursor:"pointer", letterSpacing:"0.05em" }}
          >GO</button>
        </div>
      </div>

      {/* ── ROW 2 — Info bar: ticker · company · price · change | Run ── */}
      {showInfoBar && (
        <div data-testid="ai-info-bar" style={{
          background:"#ffffff", border:"1px solid #e2e8f0", borderRadius:8,
          padding:"8px 14px",
          display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, flexWrap:"wrap",
        }}>
          <div data-testid="ai-ticker-info" style={{
            display:"flex", alignItems:"center", gap:8, flexWrap:"wrap",
            fontSize:13, color:"#0f172a",
          }}>
            <span style={{ fontFamily:"monospace", fontWeight:700 }}>{currentTicker}</span>
            {companyName && (
              <>
                <span style={{ color:"#cbd5e1" }}>·</span>
                <span style={{ color:"#475569" }}>{companyName}</span>
              </>
            )}
            {p?.ok && p?.price != null && (
              <>
                <span style={{ color:"#cbd5e1" }}>·</span>
                <span data-testid="ai-ticker-price" style={{ fontFamily:"monospace", fontWeight:600 }}>
                  ${Number(p.price).toFixed(2)}
                </span>
                {p.chgPct != null && (
                  <>
                    <span style={{ color:"#cbd5e1" }}>·</span>
                    <span
                      data-testid="ai-ticker-change"
                      style={{
                        fontFamily:"monospace", fontWeight:600,
                        color: p.chgPct >= 0 ? "#16a34a" : "#dc2626",
                      }}
                    >
                      {p.chgPct >= 0 ? "▲+" : "▼"}{Number(p.chgPct).toFixed(2)}%
                    </span>
                  </>
                )}
              </>
            )}
          </div>
          {!loading ? (
            <button
              data-testid="ai-run-btn" onClick={() => run()}
              style={T.primaryBtn}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#4338ca"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "#4f46e5"; }}
            >▸ Run AI Analysis</button>
          ) : (
            <div data-testid="ai-loading-inline" style={{ fontSize:12, color:"#64748b" }}>
              Claude is analysing {currentTicker}…
            </div>
          )}
        </div>
      )}

      {!analysis && !loading && !error && !needsKey && (
        <div data-testid="ai-empty" style={{ fontSize:12, color:"#94a3b8", fontStyle:"italic", lineHeight:1.7, padding:"0 4px" }}>
          Select a ticker above and run analysis for a fast verdict, trade levels, options play and deeper analysis.
        </div>
      )}

      {needsKey && (
        <div data-testid="ai-needs-key" style={{ background:"#fefce8", border:"1px solid #fef08a", color:"#a16207", borderRadius:6, padding:"10px 14px", fontSize:11, fontWeight:500 }}>
          AI analysis requires an Anthropic API key. Add <strong>ANTHROPIC_API_KEY</strong> to the backend <code>.env</code> file. Get a key at console.anthropic.com.
        </div>
      )}

      {error && !loading && (
        <div data-testid="ai-error" style={{ ...T.card, borderColor:"#dc2626" }}>
          <div style={{ color:"#dc2626", fontSize:13, marginBottom:8 }}>Error: {error}</div>
          <button onClick={() => run()} style={T.primaryBtn}>Try again</button>
        </div>
      )}

      {/* Pulsing skeleton while Claude generates */}
      {loading && <VerdictSkeleton />}

      {hasResult && (
        <>
          {/* ── ROW 3 — Verdict card (3-column internal grid) ─────────── */}
          <VerdictCard a={analysis} />

          {/* ── ROW 4 — Trade levels + Options play (two equal columns) ─ */}
          <div className="ai-2col" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <TradeLevelsCard levels={analysis.stock_levels} />
            <OptionsCard opt={analysis.options_quick} />
          </div>

          {/* ── ROW 5 — Deep analysis (collapsed by default) ──────────── */}
          <DeepAnalysis text={analysis.deep_analysis} />

          {/* ── ROW 6 — Slim disclaimer strip ─────────────────────────── */}
          <Disclaimer text={analysis.disclaimer} />

          {/* ── ROW 7 — Phase 3 Entry Approval gate (manual) ──────────── */}
          <div data-testid="ai-propose" style={{ display:"flex", flexDirection:"column", gap:12 }}>
            {!proposeResult && !proposing && (
              <button
                data-testid="ai-propose-btn"
                onClick={() => proposeTrade(currentTicker, "manual")}
                style={{ ...T.primaryBtn, alignSelf:"flex-start" }}
              >
                ▸ Propose this trade
              </button>
            )}

            {proposeSource === "manual" && (
              <ProposeOutcome
                proposing={proposing}
                result={proposeResult}
                error={proposeError}
                onResolved={() => { setProposeResult(null); setProposeSource(null); }}
                onDismiss={() => { setProposeResult(null); setProposeSource(null); }}
              />
            )}
          </div>
        </>
      )}

      <style>{`
        @media (max-width: 768px) {
          .ai-2col { grid-template-columns: 1fr !important; }
          .ai-verdict-grid { grid-template-columns: 1fr !important; row-gap: 18px !important; }
        }
      `}</style>
    </div>
  );
}
